import React, { useState, useMemo } from 'react';
import { usePayments } from '../hooks/usePayments';
import { useAuth } from '../contexts/AuthContext';
import { useCustomers } from '../hooks/useCustomers';
import { useProducts } from '../hooks/useProducts';
import { 
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  UserIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import SearchBar from '../components/common/SearchBar';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import type { Payment, PaymentStatus } from '../types/payment';

const Subscriptions: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { payments, loading: paymentsLoading, refundPayment } = usePayments(user?.organizationId);
  const { customers } = useCustomers();
  const { products } = useProducts();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [purposeFilter, setPurposeFilter] = useState<string>('all');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);

  // Filter payments
  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const matchesSearch = 
        payment.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
      const matchesPurpose = purposeFilter === 'all' || payment.purpose === purposeFilter;

      return matchesSearch && matchesStatus && matchesPurpose;
    });
  }, [payments, searchTerm, statusFilter, purposeFilter]);

  // Group by purpose
  const subscriptionPayments = filteredPayments.filter(p => p.purpose === 'subscription');
  const invoicePayments = filteredPayments.filter(p => p.purpose === 'invoice');
  const otherPayments = filteredPayments.filter(p => 
    p.purpose !== 'subscription' && p.purpose !== 'invoice'
  );

  // Statistics
  const stats = useMemo(() => {
    const total = payments.length;
    const completed = payments.filter(p => p.status === 'completed').length;
    const pending = payments.filter(p => p.status === 'pending' || p.status === 'processing').length;
    const failed = payments.filter(p => p.status === 'failed' || p.status === 'cancelled').length;
    const totalAmount = payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0);

    return { total, completed, pending, failed, totalAmount };
  }, [payments]);

  const getStatusBadge = (status: PaymentStatus) => {
    const badges = {
      pending: { label: 'Várakozik', icon: ClockIcon, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' },
      processing: { label: 'Feldolgozás', icon: ArrowPathIcon, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' },
      completed: { label: 'Sikeres', icon: CheckCircleIcon, color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' },
      failed: { label: 'Sikertelen', icon: XCircleIcon, color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' },
      cancelled: { label: 'Megszakítva', icon: XCircleIcon, color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
      refunded: { label: 'Visszatérítve', icon: ArrowPathIcon, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' },
      partial_refund: { label: 'Részben visszatérítve', icon: ArrowPathIcon, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' }
    };
    return badges[status];
  };

  const getPurposeLabel = (purpose: string) => {
    const labels = {
      subscription: 'Előfizetés',
      invoice: 'Számla',
      proforma: 'Díjbekérő',
      ticket_deposit: 'Ticket előleg',
      ticket_service: 'Ticket szolgáltatás',
      other: 'Egyéb'
    };
    return labels[purpose as keyof typeof labels] || purpose;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getCustomerName = (customerId?: string) => {
    if (!customerId) return '-';
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return '-';
    return customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`.trim();
  };

  const getProductName = (payment: Payment) => {
    const productId = payment.metadata?.productId;
    if (!productId) return payment.description || '-';
    const product = products.find(p => p.id === productId);
    return product?.name || payment.description || '-';
  };

  if (paymentsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <CommonPageHeader
        title={t('nav.subscriptions')}
        subtitle={t('nav.subscriptionsSubtitle')}
        icon={CreditCardIcon}
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Összes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
            <CreditCardIcon className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Sikeres</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.completed}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Folyamatban</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.pending}</p>
            </div>
            <ClockIcon className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Sikertelen</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failed}</p>
            </div>
            <XCircleIcon className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bevétel</p>
              <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                {formatCurrency(stats.totalAmount, 'HUF')}
              </p>
            </div>
            <CurrencyDollarIcon className="w-8 h-8 text-primary-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Keresés rendelésszám, ügyfél vagy leírás alapján..."
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | 'all')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Minden státusz</option>
              <option value="pending">Várakozik</option>
              <option value="processing">Feldolgozás</option>
              <option value="completed">Sikeres</option>
              <option value="failed">Sikertelen</option>
              <option value="cancelled">Megszakítva</option>
              <option value="refunded">Visszatérítve</option>
            </select>
          </div>

          {/* Purpose Filter */}
          <div>
            <select
              value={purposeFilter}
              onChange={(e) => setPurposeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Minden típus</option>
              <option value="subscription">Előfizetés</option>
              <option value="invoice">Számla</option>
              <option value="proforma">Díjbekérő</option>
              <option value="ticket_deposit">Ticket előleg</option>
              <option value="ticket_service">Ticket szolgáltatás</option>
              <option value="other">Egyéb</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rendelésszám
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ügyfél
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Termék/Szolgáltatás
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Típus
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Összeg
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Dátum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Nincs megjeleníthető fizetés
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => {
                  const statusBadge = getStatusBadge(payment.status);
                  const StatusIcon = statusBadge.icon;

                  return (
                    <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {payment.orderId}
                        </div>
                        {payment.providerPaymentId && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {payment.providerPaymentId}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {payment.customerName}
                            </div>
                            {payment.customerEmail && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {payment.customerEmail}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {getProductName(payment)}
                        </div>
                        {payment.note && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">
                            {payment.note}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                          {getPurposeLabel(payment.purpose)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(payment.amount, payment.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <CalendarIcon className="w-4 h-4 mr-1" />
                          {format(payment.createdAt, 'yyyy.MM.dd HH:mm', { locale: hu })}
                        </div>
                        {payment.paidAt && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            Fizetve: {format(payment.paidAt, 'yyyy.MM.dd HH:mm', { locale: hu })}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {payment.status === 'completed' && (
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setIsRefundModalOpen(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                          >
                            Visszatérítés
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary by Purpose */}
      {purposeFilter === 'all' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Előfizetések
            </h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {subscriptionPayments.length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {subscriptionPayments.filter(p => p.status === 'completed').length} aktív
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Számlák
            </h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {invoicePayments.length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {invoicePayments.filter(p => p.status === 'completed').length} kifizetve
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Egyéb
            </h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {otherPayments.length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {otherPayments.filter(p => p.status === 'completed').length} sikeres
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
